/**
 * 답을 기다리는(waiting) 글을 묻습니다. 멤버가 하나씩 답할 때마다 피드에 붙입니다.
 *
 * - 1.5초마다 묻고, 글이 answered · failed 가 되면 멈춥니다
 * - 90초가 지나도 끝나지 않으면 더 묻지 않고 「늦어지고 있다」고 표시합니다. 서버 작업은 계속되므로
 *   다음에 게시판을 열면 답이 붙어 있습니다
 * - 화면을 떠나면 진행 중인 요청을 취소합니다
 * - 한 번에 하나씩 묻습니다. 기다리는 글이 여럿이어도 요청이 겹쳐 쌓이지 않습니다
 */
import { useEffect, useRef } from 'react';
import { fetchPost } from '../../api/board.js';
import { isAbortError } from '../../api/client.js';
import { toFeedPost } from './boardFeed.js';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 90 * 1000;

export function usePendingPosts(posts, onPostUpdate, onPostTimeout) {
    const waitingIds = posts.filter((post) => post.status === 'waiting').map((post) => post.postsId);
    const waitingKey = waitingIds.join(',');
    const handlersRef = useRef({ onPostUpdate, onPostTimeout });
    handlersRef.current = { onPostUpdate, onPostTimeout };
    const startedAtRef = useRef(new Map());

    useEffect(() => {
        if (!waitingKey) {
            return undefined;
        }
        const ids = waitingKey.split(',').map(Number);
        const controller = new AbortController();
        let timer = null;

        for (const id of ids) {
            if (!startedAtRef.current.has(id)) {
                startedAtRef.current.set(id, Date.now());
            }
        }

        async function poll() {
            for (const id of ids) {
                if (controller.signal.aborted) {
                    return;
                }
                if (Date.now() - startedAtRef.current.get(id) > POLL_TIMEOUT_MS) {
                    handlersRef.current.onPostTimeout(id);
                    continue;
                }
                try {
                    const { post } = await fetchPost(id, { signal: controller.signal });
                    handlersRef.current.onPostUpdate(toFeedPost(post));
                } catch (error) {
                    if (isAbortError(error)) {
                        return;
                    }
                    // 한 번 놓쳐도 다음 차례에 다시 묻습니다. 계속 실패하면 위 시간 제한에 걸립니다.
                    console.error('[home] 답 확인 실패', id, error?.code ?? error?.name);
                }
            }
            if (!controller.signal.aborted) {
                timer = setTimeout(poll, POLL_INTERVAL_MS);
            }
        }

        timer = setTimeout(poll, POLL_INTERVAL_MS);
        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [waitingKey]);
}
